/**
 * Selecting vector objects by criteria rather than one click at a time.
 *
 * Traced or generated artwork arrives as hundreds of separate objects. Two
 * gestures make that editable instead of exhausting, and they are the two
 * halves this feature exists to combine:
 *
 *   - **By colour.** Every region of one colour at once. Cheap and exact,
 *     because the trace gives regions of a colour a shared `swatchId` rather
 *     than a copy of the same hex.
 *   - **By proximity.** A figure is a set of parts that touch each other or sit
 *     inside one another. `growSelection` floods outward one ring at a time
 *     from whatever you have, so you steer how far it goes.
 *
 * Grow rather than a global auto-grouping pass, deliberately. Clustering the
 * sample illustration by proximity was measured, and the useful threshold band
 * is razor-thin: at a gap of 1px it found 135 clusters, at 3px only 35, and
 * including canvas-scale background regions collapsed all 270 objects into one.
 * A global "number of groups" dial would be a lottery. Flooding from a seed the
 * user picked is the same computation made local, incremental and reversible —
 * the chaining that ruins the global version becomes something you stop at.
 *
 * Everything here is a pure `(objects, ...) => string[]`, so it is all testable
 * without a canvas.
 */

import { intersectRect, transformRectBoundsPrecise, type Rect } from '../core/geometry';
import { vectorObjectLocalBounds } from '../core/model';
import { flattenVectorObject } from './boolean';
import type { VectorObject } from './types';

/** Object bounds in layer space, or null for geometry with no extent. */
export function objectBounds(object: VectorObject): Rect | null {
  const local = vectorObjectLocalBounds(object);
  return local ? transformRectBoundsPrecise(object.transform, local) : null;
}

function fillKey(object: VectorObject): string | null {
  const fill = object.fill;
  if (!fill) return null;
  // A shared swatch is identity; a bare hex is only a coincidence of value, but
  // it is the best available for artwork that arrived without swatches.
  return fill.swatchId ? `swatch:${fill.swatchId}` : `color:${fill.color.toLowerCase()}`;
}

/** Every object painted with the same fill as anything currently selected. */
export function sameFill(objects: readonly VectorObject[], selectedIds: readonly string[]): string[] {
  const selected = new Set(selectedIds);
  const keys = new Set<string>();
  for (const object of objects) {
    if (!selected.has(object.id)) continue;
    const key = fillKey(object);
    if (key) keys.add(key);
  }
  if (keys.size === 0) return [...selectedIds];
  return objects.filter((object) => {
    const key = fillKey(object);
    return key !== null && keys.has(key);
  }).map((object) => object.id);
}

/** Every object linked to one specific global swatch. */
export function withSwatch(objects: readonly VectorObject[], swatchId: string): string[] {
  return objects.filter((object) => object.fill?.swatchId === swatchId || object.stroke?.paint.swatchId === swatchId).map((object) => object.id);
}

export interface RectSelectOptions {
  /**
   * Require the object to sit wholly inside the band. **Use this on traced
   * artwork.**
   *
   * The Illustrator reflex is that a marquee takes anything it crosses, and that
   * is what this used to recommend. On a trace it is wrong, because a trace is a
   * partition: the backdrop's bounding box IS the document, so it intersects
   * every band that can be drawn. Measured on the reference illustration — 268
   * objects — a band drawn tightly around one figure took 75 objects including
   * the backdrop when crossing, and 61 with none of it when contained. A 60x60
   * band over bare backdrop took 1 object crossing and 0 contained.
   *
   * Containment is also exact rather than approximate here: a bounding box
   * contains its geometry, so a box inside the band means the geometry is inside
   * it too — no false positives — and traced objects carry an identity
   * transform, so no false negatives either.
   */
  readonly contained?: boolean;
}

/** The marquee: objects touching (or wholly within) a dragged rectangle. */
export function withinRect(objects: readonly VectorObject[], rect: Rect, options: RectSelectOptions = {}): string[] {
  const normalized: Rect = {
    x: Math.min(rect.x, rect.x + rect.width),
    y: Math.min(rect.y, rect.y + rect.height),
    width: Math.abs(rect.width),
    height: Math.abs(rect.height),
  };
  if (normalized.width <= 0 || normalized.height <= 0) return [];
  const hits: string[] = [];
  for (const object of objects) {
    const bounds = objectBounds(object);
    if (!bounds) continue;
    if (options.contained) {
      const insideX = bounds.x >= normalized.x && bounds.x + bounds.width <= normalized.x + normalized.width;
      const insideY = bounds.y >= normalized.y && bounds.y + bounds.height <= normalized.y + normalized.height;
      if (insideX && insideY) hits.push(object.id);
    } else if (intersectRect(bounds, normalized)) {
      hits.push(object.id);
    }
  }
  return hits;
}

/**
 * Every object sharing boundary with the selection, one ring outward.
 *
 * The other half of what "grow" can mean. `growSelection` answers *which shapes
 * read as one figure* — it bridges hairline gaps deliberately and refuses to
 * walk through a background. This answers *which regions physically touch*: no
 * tolerance, no perception, no exceptions. Two regions are neighbours iff the
 * trace recorded shared boundary between them, so the background is a neighbour
 * of everything it borders, and that is the correct answer to this question.
 *
 * Only traced artwork can answer it. `neighbours` comes from the contour graph
 * at trace time; imported or hand-drawn artwork has none, and the caller should
 * offer the figure semantics instead rather than approximating this one.
 */
export function selectTouching(
  objects: readonly VectorObject[],
  selectedIds: readonly string[],
  neighbours: ReadonlyMap<string, ReadonlyMap<string, number>>,
  options: { readonly minimumSharedLength?: number } = {},
): string[] {
  const selected = new Set(selectedIds);
  if (selected.size === 0) return [];
  // No threshold by default. A hairline share is still contact, and filtering it
  // silently drops thin attachments that were drawn on purpose.
  const minimum = Math.max(0, options.minimumSharedLength ?? 0);
  const grown = new Set(selected);
  for (const id of selected) {
    const row = neighbours.get(id);
    if (!row) continue;
    for (const [neighbour, length] of row) if (length >= minimum) grown.add(neighbour);
  }
  return objects.filter((object) => grown.has(object.id)).map((object) => object.id);
}

/** Repeats {@link selectTouching} to the whole connected run of regions. */
export function selectTouchingConnected(
  objects: readonly VectorObject[],
  selectedIds: readonly string[],
  neighbours: ReadonlyMap<string, ReadonlyMap<string, number>>,
  options: { readonly minimumSharedLength?: number } = {},
): string[] {
  let current = [...selectedIds];
  for (let step = 0; step < objects.length + 1; step += 1) {
    const next = selectTouching(objects, current, neighbours, options);
    if (next.length === current.length) return next;
    current = next;
  }
  return current;
}

export function selectAllObjects(objects: readonly VectorObject[]): string[] {
  return objects.map((object) => object.id);
}

export function invertObjectSelection(objects: readonly VectorObject[], selectedIds: readonly string[]): string[] {
  const selected = new Set(selectedIds);
  return objects.filter((object) => !selected.has(object.id)).map((object) => object.id);
}

function contains(outer: Rect, inner: Rect): boolean {
  return inner.x >= outer.x && inner.y >= outer.y
    && inner.x + inner.width <= outer.x + outer.width
    && inner.y + inner.height <= outer.y + outer.height;
}

export interface GrowOptions {
  /**
   * Outline gap, in layer units, still counted as touching.
   *
   * Defaults to a thousandth of the square root of the artwork's bounding-box
   * area — the geometric mean of its sides, 1.016 for a 896x1152 illustration,
   * NOT a thousandth of its diagonal, which would be 1.459. Scaled at all
   * because contact is relative and artwork arrives at any scale. Measured on the sample file, flooding to a fixpoint
   * from seven different seeds gave figure sizes of 3, 29, 3, 3, 3, 47, 2 at
   * tolerance 1, and 3, 99, 99, 99, 99, 47, 2 at tolerance 2 — a third of the
   * picture. Contact in dense artwork is genuinely a sub-pixel question.
   */
  readonly tolerance?: number;
  /**
   * Fraction of the whole artwork above which a shape stops propagating.
   *
   * A background touches and encloses everything, so without this, growing from
   * one figure reaches the background and the next press drags in every other
   * figure through it. Measuring the sample file, including its full-canvas
   * background collapsed all 270 objects into one cluster at any threshold.
   *
   * Such a shape can still be *selected* — it just does not act as the bridge.
   */
  readonly bridgeCoverage?: number;
}

interface Shape {
  readonly id: string;
  readonly bounds: Rect;
  readonly area: number;
  /** Flattened outline points in layer space. */
  readonly points: readonly (readonly [number, number])[];
  /** Rings for containment tests: outer boundary first, then holes. */
  readonly rings: readonly (readonly (readonly [number, number])[])[];
}

/**
 * Everything `growSelection` needs, prepared once.
 *
 * Building it flattens every object's outline, so an interactive caller should
 * keep it between presses rather than rebuilding on each one.
 */
export interface ProximityIndex {
  readonly shapes: readonly Shape[];
  readonly byId: ReadonlyMap<string, Shape>;
  readonly bridgeArea: number;
  /** Outline points bucketed by cell, so a neighbour query is local. */
  readonly grid: CellGrid;
  readonly cellSize: number;
}

/**
 * Cells are keyed by row then column through nested maps rather than packed
 * into one number. Packing as `(cy + K) * K + (cx + K)` collides as soon as a
 * coordinate leaves the assumed range — cells (100000, 0) and (0, 1) landed on
 * the same key — which silently merges two distant buckets. The final distance
 * check meant that never selected the wrong object, but it quietly destroyed the
 * locality the grid exists for.
 */
type CellGrid = ReadonlyMap<number, ReadonlyMap<number, readonly GridPoint[]>>;

interface GridPoint { readonly id: string; readonly x: number; readonly y: number }

export function createProximityIndex(objects: readonly VectorObject[]): ProximityIndex {
  const outlines: { id: string; bounds: Rect; rings: (readonly [number, number])[][] }[] = [];
  for (const object of objects) {
    const bounds = objectBounds(object);
    if (!bounds) continue;
    const rings: (readonly [number, number])[][] = [];
    for (const polygon of flattenVectorObject(object)) {
      for (const ring of polygon) {
        const cleaned = ring.map(([x, y]) => [x, y] as const);
        if (cleaned.length >= 3) rings.push(cleaned);
      }
    }
    outlines.push({ id: object.id, bounds, rings });
  }
  const left = outlines.length ? Math.min(...outlines.map((s) => s.bounds.x)) : 0;
  const top = outlines.length ? Math.min(...outlines.map((s) => s.bounds.y)) : 0;
  const right = outlines.length ? Math.max(...outlines.map((s) => s.bounds.x + s.bounds.width)) : 0;
  const bottom = outlines.length ? Math.max(...outlines.map((s) => s.bounds.y + s.bounds.height)) : 0;
  // The frame everything is measured against. Shapes contribute their TRUE
  // area, so the ratio reads as "how much of the frame this shape actually
  // covers" — a full-canvas backdrop is ~1, a thin diagonal spanning the same
  // extent is near 0.
  const bridgeArea = Math.max(0, (right - left) * (bottom - top));

  // Sample outlines at half the scale contact is judged at. Sampling AT the
  // tolerance still misses by a sliver — two rectangles an exact 0.49 apart
  // measured 0.50009 through their samples and were called separate at a
  // tolerance of 0.5. Halving the step bounds that error to a quarter of the
  // tolerance; contact is a judged threshold either way, so a bounded epsilon is
  // the honest guarantee rather than an exact one.
  const step = Math.max(0.25, Math.sqrt(bridgeArea) / 2000);
  const cellSize = Math.max(1, step * 4);

  const shapes: Shape[] = [];
  const grid = new Map<number, Map<number, GridPoint[]>>();
  for (const outline of outlines) {
    const points: (readonly [number, number])[] = [];
    for (const ring of outline.rings) {
      // Along each edge, not only at its ends: a rectangle has four vertices,
      // so two long edges running a hair apart have their nearest vertices at
      // opposite corners and would read as far apart.
      for (let i = 0; i < ring.length; i += 1) {
        const [x1, y1] = ring[i]!;
        const [x2, y2] = ring[(i + 1) % ring.length]!;
        const divisions = Math.max(1, Math.ceil(Math.hypot(x2 - x1, y2 - y1) / step));
        for (let d = 0; d < divisions; d += 1) {
          const t = d / divisions;
          points.push([x1 + (x2 - x1) * t, y1 + (y2 - y1) * t] as const);
        }
      }
    }
    // True polygon area, not the bounding box. The bounding box of a thin
    // diagonal — a sash, a swoosh, a light beam, all of which a trace produces —
    // can cover most of the artwork while the shape itself covers almost none.
    // Judging "canvas-scale" by the box made such a shape a permanent bridge:
    // it could neither propagate nor be reached, so Grow could never select it
    // or anything past it, at any tolerance.
    let area = 0;
    for (const ring of outline.rings) {
      let doubled = 0;
      for (let i = 0; i < ring.length; i += 1) {
        const [ax, ay] = ring[i]!;
        const [bx, by] = ring[(i + 1) % ring.length]!;
        doubled += ax * by - bx * ay;
      }
      area += doubled / 2;
    }
    shapes.push({ id: outline.id, bounds: outline.bounds, area: Math.abs(area), points, rings: outline.rings });
    for (const [x, y] of points) {
      const cy = Math.floor(y / cellSize);
      const cx = Math.floor(x / cellSize);
      let row = grid.get(cy);
      if (!row) { row = new Map(); grid.set(cy, row); }
      const bucket = row.get(cx);
      if (bucket) bucket.push({ id: outline.id, x, y });
      else row.set(cx, [{ id: outline.id, x, y }]);
    }
  }
  return { shapes, byId: new Map(shapes.map((shape) => [shape.id, shape])), bridgeArea, grid, cellSize };
}

/** Even-odd ray cast against every ring, so holes read as outside. */
function pointInShape(shape: Shape, x: number, y: number): boolean {
  let inside = false;
  for (const ring of shape.rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
      const [xi, yi] = ring[i]!;
      const [xj, yj] = ring[j]!;
      if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
    }
  }
  return inside;
}

/**
 * Whether `inner` sits inside `outer`, by vote over several of inner's points.
 *
 * A single probe is not enough, because the obvious probe to pick is one of
 * inner's own outline points and a ray cast is ambiguous exactly on a boundary:
 * for a square spanning 0..10, the point (0,5) reads inside and (10,5) reads
 * outside. Two regions that share an edge — which traced artwork is full of —
 * hit that case constantly. Sampling several points and taking the majority
 * makes the answer stable regardless of which edge a probe lands on.
 */
function shapeInsideShape(inner: Shape, outer: Shape): boolean {
  if (!contains(outer.bounds, inner.bounds)) return false;
  const probes: (readonly [number, number])[] = [[inner.bounds.x + inner.bounds.width / 2, inner.bounds.y + inner.bounds.height / 2]];
  const stride = Math.max(1, Math.floor(inner.points.length / 8));
  for (let i = 0; i < inner.points.length && probes.length < 9; i += stride) probes.push(inner.points[i]!);
  let votes = 0;
  for (const [x, y] of probes) if (pointInShape(outer, x, y)) votes += 1;
  return votes * 2 > probes.length;
}

/**
 * Expands a selection by one ring of neighbours.
 *
 * A neighbour either touches within `tolerance` or contains/is contained by a
 * selected object. Containment matters as much as touching: in traced art the
 * details sit *on top of* larger shapes, so a highlight inside a face never
 * touches that face's outline. Measuring the sample illustration, adding
 * containment halved the number of objects left stranded on their own.
 */
/** See {@link GrowOptions.tolerance}. */
export function defaultGrowTolerance(index: ProximityIndex): number {
  return Math.max(0.5, Math.sqrt(index.bridgeArea) / 1000);
}

export function growWithIndex(index: ProximityIndex, selectedIds: readonly string[], options: GrowOptions = {}): string[] {
  const selected = new Set(selectedIds);
  if (selected.size === 0 || index.shapes.length === 0) return [...selectedIds];
  const tolerance = Math.max(0, options.tolerance ?? defaultGrowTolerance(index));
  const bridgeLimit = index.bridgeArea * Math.min(1, Math.max(0, options.bridgeCoverage ?? 0.5));

  const grown = new Set(selected);
  // A canvas-scale shape neither propagates NOR gets pulled in. Blocking only
  // propagation was not enough: the background still joined every result, so
  // "select this figure" quietly returned the figure plus the whole backdrop,
  // and dragging or recolouring that selection took the backdrop with it. On the
  // reference illustration every seed came back holding a member covering 100%
  // of the artwork. It stays selectable by clicking it — it is just not
  // something growing outward from a figure should ever reach.
  const propagates = (shape: Shape) => shape.area <= bridgeLimit;
  const seeds = index.shapes.filter((shape) => selected.has(shape.id) && propagates(shape));

  // Touching, by outline rather than by bounding box. Bounding boxes are what
  // made this useless on real artwork: dancers whose boxes overlap heavily are
  // not actually in contact, and flooding by box swallowed 255 of 270 objects.
  const reach = Math.max(1, Math.ceil(tolerance / index.cellSize));
  for (const seed of seeds) {
    for (const [x, y] of seed.points) {
      const cx = Math.floor(x / index.cellSize);
      const cy = Math.floor(y / index.cellSize);
      for (let dy = -reach; dy <= reach; dy += 1) {
        const row = index.grid.get(cy + dy);
        if (!row) continue;
        for (let dx = -reach; dx <= reach; dx += 1) {
          const bucket = row.get(cx + dx);
          if (!bucket) continue;
          for (const point of bucket) {
            if (grown.has(point.id)) continue;
            const candidate = index.byId.get(point.id);
            if (!candidate || !propagates(candidate)) continue;
            if (Math.hypot(point.x - x, point.y - y) <= tolerance) grown.add(point.id);
          }
        }
      }
    }
  }

  // Containment, which adjacency alone cannot see: a highlight painted inside a
  // face never touches that face's outline. Bounds first as a cheap reject,
  // then a real point-in-polygon so an L-shape's box does not claim its notch.
  for (const shape of index.shapes) {
    if (grown.has(shape.id) || !propagates(shape)) continue;
    if (shape.points.length === 0) continue;
    for (const seed of seeds) {
      if (shapeInsideShape(shape, seed) || shapeInsideShape(seed, shape)) { grown.add(shape.id); break; }
    }
  }

  return index.shapes.filter((shape) => grown.has(shape.id)).map((shape) => shape.id);
}

/** Expands a selection by one ring of neighbours. See {@link growWithIndex}. */
export function growSelection(objects: readonly VectorObject[], selectedIds: readonly string[], options: GrowOptions = {}): string[] {
  if (selectedIds.length === 0) return [];
  return growWithIndex(createProximityIndex(objects), selectedIds, options);
}

/** Repeats the grow until it stops adding, i.e. the whole connected figure. */
export function growSelectionToConnected(objects: readonly VectorObject[], selectedIds: readonly string[], options: GrowOptions = {}): string[] {
  if (selectedIds.length === 0) return [];
  const index = createProximityIndex(objects);
  let current = [...selectedIds];
  // Bounded by the object count, not a round number: every round that changes
  // anything adds at least one object, so this is the real ceiling. A flat 64
  // stopped one short of a 66-object chain and quietly returned a partial figure.
  const ceiling = objects.length + 1;
  for (let step = 0; step < ceiling; step += 1) {
    const next = growWithIndex(index, current, options);
    if (next.length === current.length) return next;
    current = next;
  }
  return current;
}
